import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HierarchyTrail } from '@/components/forge/HierarchyTrail'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Mono, Num } from '@/components/forge/primitives'
import { tagLabel } from '@/features/sensorCharts/tree'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'
import { SensorTagTree } from './SensorTagTree'

/**
 * Pieces the three sensor-chart screens share. LIVE.
 */

/**
 * A failed chart write, stated where it was attempted.
 *
 * The server's own sentences are never rendered — only which FIELD it named,
 * translated into our copy, and the request id. A 403 is called out because
 * its recovery is a person, not a retry: `sensor_explorer.use` may change only
 * the charts its holder created.
 */
export function ChartWriteError({ error, action, className }) {
  const { t } = useTranslation(['app', 'common'])
  if (!error) return null

  const reason =
    error.status === 403
      ? t('sensors.charts.errors.forbidden')
      : error.fields?.sensor_tag_ids
        ? t('sensors.charts.errors.tags')
        : error.fields?.name
          ? t('sensors.charts.errors.name')
          : error.fields?.plant_id || error.fields?.line_id || error.fields?.machine_id
            ? t('sensors.charts.errors.place')
            : error.code === 'notFound'
              ? t('sensors.charts.errors.gone')
              : t(`common:${error.messageKey}`)

  return (
    <div role="alert" className={cn('rounded-sm border border-hairline stripe-red bg-card p-3.5', className)}>
      <p className="m-0 text-control text-on-red">{t(`sensors.charts.errors.${action}Failed`)}</p>
      <p className="mt-1.5 mb-0 text-body-sm text-ink-dim">{reason}</p>
      {error.requestId ? (
        <Mono className="mt-2 block text-micro font-normal text-ink-dim">
          {t('common:errors.requestId', { id: error.requestId })}
        </Mono>
      ) : null}
    </div>
  )
}

/**
 * The browser body for one place: loading, refused, failed, empty, or the tree.
 * Shared by the create screen and the Add Signals dialog so the two cannot
 * disagree about what "no sensors here" looks like.
 */
export function SensorBrowser({ access, browser, ...treeProps }) {
  const { t } = useTranslation(['app', 'common'])

  if (!access.browse) {
    return (
      <EmptyState
        icon="lock"
        title={t('sensors.charts.noBrowse')}
        body={t('sensors.charts.noBrowseBody')}
        className="border-0 shadow-none"
      />
    )
  }
  if (browser.isPending) return <SkeletonRows count={4} />
  if (browser.isError) {
    return (
      <ErrorState
        error={browser.error}
        title={t('sensors.charts.browseFailed')}
        onRetry={browser.refetch}
        className="border-0 shadow-none"
      />
    )
  }
  if (!browser.tree || browser.tree.sensorCount === 0) {
    return (
      <EmptyState
        icon="gauge"
        title={t('sensors.charts.noSensors')}
        body={t('sensors.charts.noSensorsBody')}
        className="border-0 shadow-none"
      />
    )
  }
  return (
    <div className="overflow-hidden rounded-sm border border-hairline">
      <SensorTagTree key={browser.tree.key} tree={browser.tree} unitSymbol={browser.unitSymbol} {...treeProps} />
    </div>
  )
}

/** One saved chart as a card: name, where it hangs, which tags, when made. */
export function ChartCard({ chart }) {
  const { t } = useTranslation(['app', 'common'])
  const shown = chart.sensorTags.slice(0, 3)
  const rest = chart.sensorTags.length - shown.length

  return (
    <Panel
      as={Link}
      to={`/app/sensors/charts/${chart.id}`}
      interactive
      className="flex h-full flex-col gap-2.5 no-underline"
    >
      <div className="flex items-center gap-2">
        <Icon name="chart" size="sm" className="text-on-forge" />
        <span className="ms-auto" />
        <Icon name="right" size="xs" className="text-ink-mute mirror-rtl" />
      </div>
      <p className="m-0 text-title">{chart.name}</p>
      <HierarchyTrail hierarchy={chart.hierarchy} emptyLabel={t('sensors.charts.placeGone')} />
      <p className="m-0 text-body-sm text-ink-dim">
        {chart.sensorTags.length
          ? [
              ...shown.map((tag) => tagLabel(tag, t)),
              ...(rest > 0 ? [t('sensors.charts.moreTags', { rest })] : []),
            ].join(' · ')
          : t('sensors.charts.noTagsOnChart')}
      </p>
      <p className="m-0 mt-auto text-micro font-normal text-ink-dim">
        <Num>
          {t('sensors.charts.cardMeta', {
            tags: chart.sensorTags.length,
            when: chart.createdAt ? formatRelative(chart.createdAt) : '—',
          })}
        </Num>
      </p>
    </Panel>
  )
}

/* The plot box inside `WorkstationChart`'s 1000×340 viewBox, as percentages of
   it — PAD 56/56 across, 22 above, 26 below. Kept in step by hand; the two
   only need to agree closely enough that nothing jumps when the trace lands. */
const PLOT_BOX = { start: '5.6%', end: '5.6%', top: '6.5%', bottom: '7.6%' }
const GRID_ROWS = [0, 0.25, 0.5, 0.75, 1]
const TIME_TICKS = 5

/**
 * The chart, before it has readings: the same box `WorkstationChart` will fill.
 *
 * A row skeleton here would be the wrong shape twice — card-shaped where a plot
 * is coming, and a different height, so the page jumps when the trace lands.
 * This holds the plot's aspect, its gridlines and the two label rows under it,
 * so the only thing that changes on arrival is that the lines appear.
 *
 * The gridlines are static; only the axis and time labels pulse. A whole plot
 * pulsing at this size is a flashing rectangle, not a sign of loading.
 *
 * `fill` matches `WorkstationChart`'s: the box takes the parent's height
 * instead of holding the aspect.
 */
export function ChartSkeleton({ fill = false, className }) {
  const { t } = useTranslation()
  return (
    <div className={cn('m-0', fill && 'flex h-full min-h-0 flex-col', className)} aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('common:loading')}</span>
      <div aria-hidden="true" className={cn(fill && 'flex min-h-0 flex-1 flex-col')}>
        <div className={cn('relative w-full', fill ? 'min-h-[12rem] flex-1 basis-0' : 'aspect-[1000/340]')}>
          <div
            className="absolute rounded-xs bg-sunken/40"
            style={{
              insetInlineStart: PLOT_BOX.start,
              insetInlineEnd: PLOT_BOX.end,
              top: PLOT_BOX.top,
              bottom: PLOT_BOX.bottom,
            }}
          >
            {GRID_ROWS.map((row) => (
              <span
                key={row}
                className="absolute inset-x-0 h-px bg-hairline"
                style={{ top: `${row * 100}%` }}
              />
            ))}
          </div>
          {GRID_ROWS.map((row) => (
            <span
              key={row}
              className="absolute start-0 h-2.5 w-8 -translate-y-1/2 animate-pulse rounded-xs bg-sunken"
              style={{ top: `calc(${PLOT_BOX.top} + (100% - ${PLOT_BOX.top} - ${PLOT_BOX.bottom}) * ${row})` }}
            />
          ))}
        </div>
        <div className="mt-1 h-3 w-16 animate-pulse rounded-xs bg-sunken" />
        <div className="mt-1.5 flex h-4 items-center justify-between">
          {Array.from({ length: TIME_TICKS }, (_, index) => (
            <span key={index} className="h-2.5 w-14 animate-pulse rounded-xs bg-sunken" />
          ))}
        </div>
      </div>
    </div>
  )
}
